import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2, HeartHandshake, Crown, CheckCircle2, XCircle, Clock, CalendarClock, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { errText } from "@/lib/errText";
import { daysUntil } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck } from "@/components/common/TrustBadge";
import { TierCard } from "@/components/creator/TierCard";
import { Button } from "@/components/ui/button";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
const MAX_POLLS = 12;
const POLL_INTERVAL = 1500;
const SLOW_INTERVAL = 5000;
const SLOW_MAX = 24; // ~2 more minutes of quiet background recovery after the fast burst

// Post-Stripe-checkout banner on the memberships hub.
function ReturnBanner({ state, onRefresh, checking }) {
  if (!state) return null;
  const map = {
    polling: { icon: Loader2, cls: "border-white/15 bg-white/5 text-bloq-muted", title: "Confirming your subscription…", sub: "Hang tight, this only takes a moment.", spin: true },
    success: { icon: CheckCircle2, cls: "border-bloq-mint/40 bg-bloq-mint/10 text-bloq-mint", title: "You're subscribed! 🎉", sub: "Your membership is active. Thanks for supporting a creator." },
    pending: { icon: Clock, cls: "border-amber-400/40 bg-amber-400/10 text-amber-300", title: "Payment processing", sub: "This can take a moment — we'll keep checking automatically. You can also check now.", refresh: true },
    canceled: { icon: XCircle, cls: "border-white/15 bg-white/5 text-bloq-muted", title: "Checkout canceled", sub: "No charge was made. You can subscribe anytime." },
    error: { icon: XCircle, cls: "border-destructive/40 bg-destructive/10 text-destructive", title: "Something went wrong", sub: "We couldn't confirm the checkout. Please try again." },
  };
  const c = map[state] || map.error;
  const Icon = c.icon;
  return (
    <div className={cn("mb-4 flex items-start gap-3 rounded-2xl border p-4", c.cls)} data-testid="tier-checkout-banner">
      <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", c.spin && "animate-spin")} />
      <div className="flex-1">
        <div className="text-sm font-bold">{c.title}</div>
        <div className="text-xs opacity-90">{c.sub}</div>
        {c.refresh && (
          <button onClick={onRefresh} disabled={checking} className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold underline underline-offset-2 hover:opacity-80 disabled:opacity-50" data-testid="tier-checkout-refresh">
            {checking && <Loader2 className="h-3 w-3 animate-spin" />} Check again
          </button>
        )}
      </div>
    </div>
  );
}

// Fan side of Lane B. Two modes:
//  - subscribe view  (route /app/creators/:userId): show a creator's active tiers + Subscribe.
//  - memberships hub (route /creators, top-level): post-checkout return poll + my subscriptions.
export default function CreatorMemberships() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscribing, setSubscribing] = useState(null);
  const [banner, setBanner] = useState(null);
  const [checking, setChecking] = useState(false);
  const pollCount = useRef(0);
  const sessionRef = useRef(null);
  const stoppedRef = useRef(false);
  const timerRef = useRef(null);
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("bloq_token");

  // --- subscribe view ---
  const { data: tierData, isLoading: tiersLoading, isError } = useQuery({
    queryKey: ["creator-public-tiers", userId],
    queryFn: () => api.creatorTiers(userId),
    enabled: !!userId && hasToken,
    retry: false,
  });

  // --- hub view (no userId) ---
  const { data: subsData, isLoading: subsLoading } = useQuery({
    queryKey: ["my-subscriptions"],
    queryFn: api.mySubscriptions,
    enabled: !userId && hasToken,
    retry: false,
  });

  // Post-checkout return handling (hub only). Fast burst, then quiet background auto-recovery.
  const settleSuccess = () => {
    stoppedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    setBanner("success");
    qc.invalidateQueries({ queryKey: ["my-subscriptions"] });
    setSearchParams({}, { replace: true });
  };

  useEffect(() => {
    if (userId) return;
    const session = searchParams.get("tier_session");
    const canceled = searchParams.get("tier_canceled");
    if (canceled) {
      setBanner("canceled");
      setSearchParams({}, { replace: true });
      return;
    }
    if (!session) return;
    sessionRef.current = session;
    stoppedRef.current = false;
    pollCount.current = 0;
    let slow = 0;
    setBanner("polling");
    const poll = async () => {
      if (stoppedRef.current) return;
      pollCount.current += 1;
      try {
        const res = await api.creatorSubscribeStatus(session);
        if (res.granted || res.payment_status === "paid") { settleSuccess(); return; }
      } catch {
        /* keep trying */
      }
      if (stoppedRef.current) return;
      if (pollCount.current < MAX_POLLS) {
        timerRef.current = setTimeout(poll, POLL_INTERVAL);
      } else {
        setBanner("pending");
        if (slow < SLOW_MAX) { slow += 1; timerRef.current = setTimeout(poll, SLOW_INTERVAL); }
      }
    };
    poll();
    return () => { stoppedRef.current = true; if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Manual "Check again" — re-polls the exact checkout session immediately.
  const checkAgain = async () => {
    const session = sessionRef.current;
    if (checking || stoppedRef.current || !session) return;
    setChecking(true);
    try {
      const res = await api.creatorSubscribeStatus(session);
      if (res.granted || res.payment_status === "paid") settleSuccess();
    } catch {
      /* stay pending — background recovery continues */
    } finally {
      setChecking(false);
    }
  };

  if (!hasToken) return <Navigate to="/login" replace />;

  const subscribe = async (tier) => {
    if (subscribing) return;
    setSubscribing(tier.id);
    try {
      const res = await api.subscribeCreator(userId, { tier_id: tier.id, origin_url: window.location.origin });
      if (res.url) {
        window.location.href = res.url;
        return;
      }
      toast.error("Couldn't start checkout.");
    } catch (e) {
      const st = e?.response?.status;
      if (st === 400) toast.error("You can't subscribe to your own tier.");
      else if (st === 409) toast.error("You're already subscribed to this tier.");
      else if (st === 503) toast.error("This creator isn't set up to accept payments yet.");
      else if (st === 404) toast.error("That tier is no longer available.");
      else toast.error(errText(e, "Couldn't start checkout."));
    } finally {
      setSubscribing(null);
    }
  };

  // ============ SUBSCRIBE VIEW ============
  if (userId) {
    const creator = tierData?.creator;
    const tiers = (tierData?.tiers || []).filter((t) => t.active);
    const currentTierId = tierData?.my_subscription?.tier_id || null;
    return (
      <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="creator-subscribe-page">
        <div className="mx-auto max-w-3xl px-5 py-6 sm:px-8">
          <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm text-bloq-muted hover:text-white" data-testid="subscribe-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          {tiersLoading ? (
            <div className="flex justify-center py-20 text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : isError || !tierData?.is_creator ? (
            <div className="rounded-3xl border border-white/10 bg-bloq-s1 p-8 text-center" data-testid="not-a-creator">
              <Crown className="mx-auto h-7 w-7 text-bloq-muted" />
              <p className="mt-2 text-sm text-bloq-muted">This person isn't offering memberships right now.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4" data-testid="subscribe-creator-header">
                {creator && <Avatar user={creator} size="lg" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h1 className="truncate text-2xl font-bold">{creator?.display_name || `@${creator?.username}`}</h1>
                    {creator?.verified && <VerifiedCheck size={16} />}
                  </div>
                  <p className="text-sm text-bloq-muted">@{creator?.username} · Support with a monthly membership</p>
                </div>
              </div>

              {tiers.length === 0 ? (
                <div className="mt-6 rounded-3xl border border-white/10 bg-bloq-s1 p-8 text-center" data-testid="subscribe-no-tiers">
                  <p className="text-sm text-bloq-muted">No membership tiers are available yet.</p>
                </div>
              ) : (
                <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="subscribe-tiers">
                  {tiers.map((t) => (
                    <TierCard
                      key={t.id}
                      tier={t}
                      isCurrent={currentTierId === t.id}
                      onSubscribe={subscribe}
                      subscribing={subscribing === t.id}
                    />
                  ))}
                </div>
              )}
              <p className="mt-6 text-center text-xs text-bloq-muted">
                Renews monthly. Payments are processed securely by Stripe. Manage or cancel from your Memberships page.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  // ============ MEMBERSHIPS HUB VIEW ============
  const subs = subsData?.subscriptions || [];
  const expiringSoon = subs.filter((s) => { const d = daysUntil(s.expires_at); return d !== null && d <= 5; }).length;
  return (
    <div className="min-h-screen overflow-y-auto bg-bloq-bg" data-testid="memberships-hub-page">
      <div className="mx-auto max-w-2xl px-5 py-6 sm:px-8">
        <button onClick={() => navigate("/app/profile")} className="mb-4 flex items-center gap-1 text-sm text-bloq-muted hover:text-white" data-testid="memberships-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-bloq-purple/15 text-bloq-purple"><HeartHandshake className="h-6 w-6" /></span>
          <div>
            <h1 className="text-2xl font-bold">Memberships</h1>
            <p className="mt-0.5 text-sm text-bloq-muted">Creators you support</p>
          </div>
        </div>

        <div className="mt-6">
          <ReturnBanner state={banner} onRefresh={checkAgain} checking={checking} />

          {expiringSoon > 0 && (
            <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-4 text-amber-300" data-testid="renewal-nudge-banner">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <div className="text-sm font-bold">{expiringSoon} membership{expiringSoon > 1 ? "s" : ""} expiring soon</div>
                <div className="text-xs opacity-90">Renew below to keep your perks — there's no auto-billing yet.</div>
              </div>
            </div>
          )}

          {subsLoading ? (
            <div className="flex justify-center py-16 text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : subs.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-white/10 py-12 text-center" data-testid="memberships-empty">
              <HeartHandshake className="mx-auto h-7 w-7 text-bloq-muted" />
              <p className="mt-2 text-sm text-bloq-muted">You're not supporting any creators yet.</p>
              <p className="text-xs text-bloq-muted">When you subscribe to a creator's tier, it'll show up here.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="memberships-list">
              {subs.map((s, i) => {
                const dLeft = daysUntil(s.expires_at);
                const expired = dLeft !== null && dLeft < 0;
                const atRisk = dLeft !== null && dLeft <= 5;
                const expiryLabel = dLeft === null ? null
                  : expired ? `expired ${fmtDate(s.expires_at)}`
                  : dLeft === 0 ? "expires today"
                  : `renews in ${dLeft}d`;
                return (
                  <div key={s.creator?.id || i} className={cn("flex items-center gap-3 rounded-2xl border p-4", atRisk ? "border-amber-400/40 bg-amber-400/[0.05]" : "border-white/8 bg-bloq-s1")} data-testid={`membership-row-${s.creator?.id || i}`}>
                    {s.creator && <Avatar user={s.creator} size="md" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-semibold text-white">{s.creator?.display_name || `@${s.creator?.username}`}</span>
                        {s.creator?.verified && <VerifiedCheck size={12} />}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-xs text-bloq-muted">
                        <Crown className="h-3 w-3 text-bloq-purple" />
                        <span className="truncate">{s.tier?.name}</span>
                        {expiryLabel && (
                          <span className={cn("flex items-center gap-1", atRisk ? "font-semibold text-amber-300" : "")} data-testid={`membership-expiry-${s.creator?.id || i}`}>
                            <CalendarClock className="h-3 w-3" /> {expiryLabel}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate(`/app/creators/${s.creator?.id}`)}
                      className={cn("shrink-0 rounded-full text-xs font-semibold", atRisk ? "bg-amber-400 text-bloq-bg hover:bg-amber-400/90" : "border border-white/12 bg-transparent text-bloq-mint hover:bg-white/5")}
                      data-testid={`membership-manage-${s.creator?.id || i}`}
                    >
                      {atRisk ? "Renew" : "Manage"}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          <p className="mt-6 text-center text-xs text-bloq-muted">
            Memberships renew monthly. There's no auto-billing yet — renew from a creator's page before the date shown.
          </p>
        </div>
      </div>
    </div>
  );
}
